import React from 'react';
import { 
  FiMessageCircle, FiCheckCircle, FiTag, FiAlertTriangle, FiActivity
} from 'react-icons/fi';
import { Line, Doughnut } from 'react-chartjs-2';

function CRMDashboard({ user, dashboardData }) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div style={{background: 'var(--gradient-primary)'}} className="rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 text-white">CRM Dashboard</h1>
            <p className="text-white opacity-90">Customer relationship management and communication analytics</p>
          </div>
          <div className="hidden md:block">
            <div className="rounded-lg p-4">
              <div className="text-white text-sm opacity-75">Welcome back,</div>
              <div className="text-white font-semibold">{user?.user_full_name}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border hover:shadow-md transition-shadow" style={{borderColor: 'var(--border-light)'}}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold mb-1" style={{color: 'var(--color-primary)'}}>{dashboardData.total_chats || 0}</div>
              <div className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>Total Chats</div>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#e3f2fd'}}>
              <FiMessageCircle className="w-6 h-6" style={{color: '#0086D4'}} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border hover:shadow-md transition-shadow" style={{borderColor: 'var(--border-light)'}}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold mb-1" style={{color: 'var(--color-success)'}}>{dashboardData.chat_response_rate || 0}%</div>
              <div className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>Response Rate</div>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#d4edda'}}>
              <FiCheckCircle className="w-6 h-6" style={{color: '#28a745'}} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border hover:shadow-md transition-shadow" style={{borderColor: 'var(--border-light)'}}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold mb-1" style={{color: 'var(--color-warning)'}}>{(dashboardData.posts || []).length || dashboardData.post_engagement?.total_posts || 0}</div>
              <div className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>Active Posts</div>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#fff3cd'}}>
              <FiTag className="w-6 h-6" style={{color: '#ffc107'}} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border hover:shadow-md transition-shadow" style={{borderColor: 'var(--border-light)'}}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold mb-1" style={{color: 'var(--color-info)'}}>{dashboardData.active_notices || 0}</div>
              <div className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>Active Notices</div>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#e1f5fe'}}>
              <FiAlertTriangle className="w-6 h-6" style={{color: '#4EADE2'}} />
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Chat Trends */}
      <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
        <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
          <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Weekly Chat Trends</h3>
        </div>
        <div className="p-4">
          <Line
            data={{
              labels: (dashboardData.weekly_chat_trends?.length > 0 && dashboardData.weekly_chat_trends.some(t => t.total_chats > 0)) ? dashboardData.weekly_chat_trends.map(trend => trend.day) : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
              datasets: [
                {
                  label: 'Total Chats',
                  data: (dashboardData.weekly_chat_trends?.length > 0 && dashboardData.weekly_chat_trends.some(t => t.total_chats > 0)) ? dashboardData.weekly_chat_trends.map(trend => trend.total_chats) : [12, 19, 15, 22, 18, 8, 14],
                  borderColor: '#4EADE2',
                  backgroundColor: 'rgba(78, 173, 226, 0.1)',
                  fill: true,
                  tension: 0.4
                },
                {
                  label: 'Answered Chats',
                  data: (dashboardData.weekly_chat_trends?.length > 0 && dashboardData.weekly_chat_trends.some(t => t.answered_chats > 0)) ? dashboardData.weekly_chat_trends.map(trend => trend.answered_chats) : [10, 16, 12, 18, 15, 6, 11],
                  borderColor: '#28a745',
                  backgroundColor: 'rgba(40, 167, 69, 0.1)',
                  fill: true,
                  tension: 0.4
                },
                {
                  label: 'Missed Chats',
                  data: (dashboardData.weekly_chat_trends?.length > 0 && dashboardData.weekly_chat_trends.some(t => t.missed_chats > 0)) ? dashboardData.weekly_chat_trends.map(trend => trend.missed_chats) : [2, 3, 3, 4, 3, 2, 3],
                  borderColor: '#dc3545',
                  backgroundColor: 'rgba(220, 53, 69, 0.1)',
                  fill: true,
                  tension: 0.4
                }
              ]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom'
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  grid: {
                    color: 'rgba(0,0,0,0.1)'
                  }
                },
                x: {
                  grid: {
                    display: false
                  }
                }
              }
            }}
            height={250}
          />
        </div>
      </div>

      {/* Chat Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat Volume by Channel */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Chat Channels</h3>
          </div>
          <div className="p-4">
            <div className="space-y-4">
              {(() => {
                const categoryData = dashboardData.chat_volume_by_category || [];
                const webData = categoryData.find(cat => cat.category === 1) || {total_chats: 0, answered_chats: 0};
                const whatsappData = categoryData.find(cat => cat.category === 2) || {total_chats: 0, answered_chats: 0};
                const helpdeskData = categoryData.find(cat => cat.category === 3) || {total_chats: 0, answered_chats: 0};
                
                return (
                  <>
                    <div className="flex items-center justify-between p-3 rounded" style={{backgroundColor: 'var(--bg-secondary)'}}>
                      <div>
                        <div className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Web Chat</div>
                        <div className="text-xs" style={{color: 'var(--text-secondary)'}}>{webData.answered_chats}/{webData.total_chats} answered</div>
                      </div>
                      <div className="text-lg font-bold" style={{color: 'var(--color-primary)'}}>{webData.total_chats}</div>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded" style={{backgroundColor: 'var(--bg-secondary)'}}>
                      <div>
                        <div className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>WhatsApp</div>
                        <div className="text-xs" style={{color: 'var(--text-secondary)'}}>{whatsappData.answered_chats}/{whatsappData.total_chats} answered</div>
                      </div>
                      <div className="text-lg font-bold" style={{color: 'var(--color-success)'}}>{whatsappData.total_chats}</div>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded" style={{backgroundColor: 'var(--bg-secondary)'}}>
                      <div>
                        <div className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Helpdesk</div>
                        <div className="text-xs" style={{color: 'var(--text-secondary)'}}>{helpdeskData.answered_chats}/{helpdeskData.total_chats} answered</div>
                      </div>
                      <div className="text-lg font-bold" style={{color: 'var(--color-warning)'}}>{helpdeskData.total_chats}</div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Chat Performance Metrics */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Performance Metrics</h3>
          </div>
          <div className="p-4">
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-2xl font-bold mb-1" style={{color: 'var(--color-info)'}}>{dashboardData.avg_response_time || 0}m</div>
                <div className="text-xs" style={{color: 'var(--text-secondary)'}}>Avg Response Time</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-lg font-bold" style={{color: 'var(--color-success)'}}>{dashboardData.answered_chats || 0}</div>
                  <div className="text-xs" style={{color: 'var(--text-secondary)'}}>Answered</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold" style={{color: 'var(--color-danger)'}}>{dashboardData.missed_chats || 0}</div>
                  <div className="text-xs" style={{color: 'var(--text-secondary)'}}>Missed</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-lg font-bold" style={{color: 'var(--color-primary)'}}>{dashboardData.authenticated_chats || 0}</div>
                  <div className="text-xs" style={{color: 'var(--text-secondary)'}}>Authenticated</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold" style={{color: 'var(--color-accent)'}}>{dashboardData.anonymous_chats || 0}</div>
                  <div className="text-xs" style={{color: 'var(--text-secondary)'}}>Anonymous</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Today's Activity */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Today's Activity</h3>
          </div>
          <div className="p-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{color: 'var(--text-secondary)'}}>Chat Volume</span>
                <span className="font-bold" style={{color: 'var(--color-primary)'}}>{dashboardData.total_chats || dashboardData.total_chat_volume || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{color: 'var(--text-secondary)'}}>Customer Logins</span>
                <span className="font-bold" style={{color: 'var(--color-info)'}}>{dashboardData.total_logins || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{color: 'var(--text-secondary)'}}>Post Views</span>
                <span className="font-bold" style={{color: 'var(--color-warning)'}}>{dashboardData.post_engagement?.total_views || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{color: 'var(--text-secondary)'}}>Notice Views</span>
                <span className="font-bold" style={{color: 'var(--color-success)'}}>{dashboardData.notice_performance?.total_views || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Performance Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chat Response Analysis */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Chat Response Analysis</h3>
          </div>
          <div style={{height: '280px', padding: '16px 16px 0 16px'}}>
            <Doughnut
              data={{
                labels: ['Answered', 'Missed', 'Authenticated', 'Anonymous'],
                datasets: [
                  {
                    data: [
                      dashboardData.answered_chats || 0,
                      dashboardData.missed_chats || 0,
                      dashboardData.authenticated_chats || 0,
                      dashboardData.anonymous_chats || 0
                    ],
                    backgroundColor: [
                      '#28a745',
                      '#dc3545',
                      '#0086D4',
                      '#B0796D'
                    ],
                    borderWidth: 0
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      fontSize: 10
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Top Performing Notices */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: 'var(--border-light)'}}>
          <div className="p-4 border-b" style={{borderColor: 'var(--border-light)'}}>
            <h3 className="text-sm font-medium" style={{color: 'var(--text-primary)'}}>Top Performing Notices</h3>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              {(dashboardData.notices || dashboardData.notice_performance?.top_notices || []).slice(0, 5).map((notice, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded" style={{backgroundColor: 'var(--bg-secondary)'}}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" style={{color: 'var(--text-primary)'}}>{notice.notice_title}</div>
                  </div>
                  <div className="flex items-center text-xs" style={{color: 'var(--text-secondary)'}}>
                    <FiActivity className="w-3 h-3 mr-1" />
                    {notice.views || 0}
                  </div>
                </div>
              ))}
              {!(dashboardData.notices || dashboardData.notice_performance?.top_notices || []).length && (
                <div className="text-center py-8" style={{color: 'var(--text-secondary)'}}>
                  No notice data available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CRMDashboard;
